import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiCall, ApiError } from '../utils/apiHelpers';
import type { IndustryDoc, SchemaDoc, SchemaStatus } from '../types';

export function SchemaListPage() {
  const { industryId } = useParams<{ industryId: string }>();
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState<SchemaDoc[]>([]);
  const [industry, setIndustry] = useState<IndustryDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!industryId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [list, ind] = await Promise.all([
          apiCall<SchemaDoc[]>(`/api/industries/${industryId}/schemas`),
          apiCall<IndustryDoc>(`/api/industries/${industryId}`),
        ]);
        if (cancelled) return;
        setSchemas(list);
        setIndustry(ind);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [industryId]);

  if (!industryId) {
    return <p style={{ color: 'var(--danger-color)' }}>Missing industry id.</p>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Link to="/industries" style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'none' }}>
            ← Programs
          </Link>
          <h1 style={{ margin: '4px 0 0' }}>
            Schemas{industry ? ` · ${industry.name}` : ''}
          </h1>
          {industry && (
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '4px 0 0' }}>
              Program type: {industry.programType}
            </p>
          )}
        </div>
        <button
          className="btn btn--primary"
          onClick={() => navigate(`/industries/${industryId}/schemas/new`)}
        >
          + New schema
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee', border: '1px solid #fcc', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : schemas.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>
          No schemas yet. Create one to define what data your QR codes will carry.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {schemas.map((s) => {
            const isActive = industry?.activeSchemaVersion === s.id;
            return (
              <Link
                key={s.id}
                to={`/industries/${industryId}/schemas/${s.id}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 8,
                  padding: 16,
                  textDecoration: 'none',
                  color: 'inherit',
                  gap: 16,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0 }}>{s.name}</h3>
                    <StatusBadge status={s.status} />
                    {isActive && (
                      <span
                        style={{
                          padding: '2px 8px',
                          fontSize: 11,
                          fontWeight: 600,
                          borderRadius: 999,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          background: 'var(--success-color, #10b981)',
                          color: '#fff',
                        }}
                      >
                        Active
                      </span>
                    )}
                  </div>
                  {s.description && (
                    <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '4px 0' }}>{s.description}</p>
                  )}
                  {s.complianceFrameworks.length > 0 && (
                    <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '4px 0 0' }}>
                      Compliance: {s.complianceFrameworks.join(', ')}
                    </p>
                  )}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div>v{s.version}</div>
                  <div>
                    {s.properties.length} field{s.properties.length === 1 ? '' : 's'}
                  </div>
                  <div>{s.encodingMode}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function StatusBadge({ status }: { status: SchemaStatus }) {
  const styles: Record<SchemaStatus, React.CSSProperties> = {
    draft: { background: 'var(--border-color)', color: 'var(--text-color)' },
    published: { background: 'var(--success-color, #10b981)', color: '#fff' },
    archived: { background: 'var(--border-color)', color: 'var(--text-muted)', fontStyle: 'italic' },
    revoked: { background: 'var(--danger-color, #ef4444)', color: '#fff' },
  };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 999,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        ...styles[status],
      }}
    >
      {status}
    </span>
  );
}
